/**
 * The copy-variant model the Concept Workbench's Content section reads.
 *
 * ── ONE VARIANT = ONE `ad_units` ROW ──────────────────────────────────────
 * A variant is a coherent copy OPTION — a headline, a caption and a CTA that
 * were written together, in one voice, in one model call. That is exactly what
 * an `ad_units` row already is (`CONCEPT_FIELD_CONTRACT` names `ad_units.copy`
 * as the home of "Primary, headline, and description variants"), so N variants
 * are N sibling rows sharing a `project_task_id`, not a new table and not a
 * blob inside one row.
 *
 * Keeping the row as the unit is what makes "More like this" cheap: an anchor
 * is a row id, the siblings it produces are more rows, and the existing review
 * state machine (`transition_ad_unit_review`) already accepts or rejects one
 * variant at a time.
 */

import type { AdPlatformId } from '@/constants/ad-specs';
import {
  COPY_FIELD_LABELS,
  type ConceptDeliveryTarget,
  type CopyField,
  tightestLimit,
} from '@/lib/concept-copy/platform-limits';
import { countCharacters, countWords } from '@/lib/concept-copy/text-metrics';

export interface ConceptCopyFieldValue {
  field: CopyField;
  /** `CopyVariantCard.field` — 'Headline' | 'Caption' | 'CTA'. */
  label: string;
  text: string;
  /** Grapheme-safe. See `text-metrics.ts` for why not `text.length`. */
  characters: number;
  words: number;
}

/**
 * The Brief decision and evidence availability captured when this option was
 * generated. This is provenance, not a performance report and not a substitute
 * for the live Brief evidence reader.
 */
export type ConceptCopyBriefEvidenceState = 'linked' | 'no_decision' | 'no_evidence';

export interface ConceptCopyBriefEvidenceLineage {
  state: ConceptCopyBriefEvidenceState;
  decision: {
    audience: string | null;
    offer: string | null;
    hypothesis: string | null;
    testVariable: string | null;
    channelRole: 'paid' | 'organic' | 'shared_strategy' | null;
    successMeasure: string | null;
    version: number;
    updatedAt: string;
  } | null;
  sourceLinkIds: string[];
  sourceCount: number;
  limitations: string[];
  capturedAt: string;
}

export interface ConceptCopyVariant {
  /** The `ad_units.id`. Stable, and what "More like this" anchors on. */
  id: string;
  conceptId: string;
  briefId: string | null;
  adPlatform: AdPlatformId;
  fields: ConceptCopyFieldValue[];
  /**
   * The generator's OWN reading of the voice it wrote in ("Confident, direct").
   *
   * Absent whenever the model did not return one. It is never inferred from the
   * text by keyword matching: a tone label the model did not assert is a
   * fabricated attribute printed on a surface a marketer will trust, which is
   * the specific failure `CopyVariantCard`'s prop doc calls out.
   */
  tone?: string;
  /** The variant this one was generated to vary from, or null for a fresh set. */
  anchoredOnVariantId: string | null;
  status: 'draft' | 'approved' | 'rejected' | 'exported';
  createdAt: string;
  /** Absent on options generated before the evidence-lineage contract. */
  briefEvidenceLineage?: ConceptCopyBriefEvidenceLineage;
}

/**
 * Exactly `CopyVariantCardProps`' data half.
 *
 * `tone`, `maxChars` and `platformLabel` are OMITTED rather than set to
 * undefined when they do not apply — the card branches on their presence, and a
 * present-but-undefined tone would still be an assertion this layer cannot
 * back.
 */
export interface CopyVariantCardData {
  field: string;
  text: string;
  tone?: string;
  maxChars?: number;
  /** Where the platform folds the text behind "see more"; a note, not an error. */
  foldChars?: number;
  platformLabel?: string;
}

export function measureField(field: CopyField, text: string): ConceptCopyFieldValue {
  return {
    field,
    label: COPY_FIELD_LABELS[field],
    text,
    characters: countCharacters(text),
    words: countWords(text),
  };
}

/**
 * Build the props for one card.
 *
 * The limit passed down is the TIGHTEST across the Concept's declared delivery
 * targets, so the platform named in "will truncate on X" is the first one the
 * copy actually breaks on.
 */
export function toCopyVariantCardData(
  variant: ConceptCopyVariant,
  field: CopyField,
  targets: readonly ConceptDeliveryTarget[],
): CopyVariantCardData | null {
  const value = variant.fields.find((entry) => entry.field === field);
  if (!value) return null;

  const data: CopyVariantCardData = { field: value.label, text: value.text };
  if (variant.tone) data.tone = variant.tone;

  const limit = tightestLimit(field, targets);
  if (limit) {
    data.maxChars = limit.maxChars;
    if (limit.foldChars !== undefined) data.foldChars = limit.foldChars;
    data.platformLabel = limit.platformLabel;
  }
  return data;
}
